import { MonitoreoReferencia } from "../../../generated/grpc/monitoreo_sesion_pb";
import { MESES_ABR } from "../../../utils/fechaUtils";
import { SesionDashboardItem } from "./response/SesionDashboardResponse";

/** Una semana ISO del histórico, con las sesiones que cayeron dentro ya
 *  combinadas. Es la unidad del eje de tiempo del dashboard: el monitoreo se
 *  planifica por semana (un recorrido semanal o quincenal por lote), así que
 *  varias sesiones de la misma semana son el mismo punto en el tiempo. */
export type SemanaMonitoreo = {
  /** Año ISO de la semana (no siempre coincide con el del calendario: la
   *  semana 1 puede empezar en diciembre). */
  anio: number;
  /** Semana ISO. */
  semana: number;
  /** Fecha del último recorrido de la semana. Representa al grupo donde hace
   *  falta un punto concreto en el tiempo, como la regresión de la proyección.
   *  **No usarla para etiquetar la semana**: es la fecha de una salida, y con
   *  varias en la semana sería una elegida al azar. Para eso están
   *  `inicio`/`fin`. */
  fecha: string;
  /** Lunes de la semana ISO (`yyyy-MM-dd`). */
  inicio: string;
  /** Domingo de la semana ISO (`yyyy-MM-dd`). */
  fin: string;
  /** Sesiones que quedaron agrupadas, de la más vieja a la más nueva. */
  sesiones: SesionDashboardItem[];
  /** Alguna sesión de la semana quedó sin finalizar. */
  tieneSinFinalizar: boolean;
  /** `datos[loteId]`, combinando las sesiones de la semana. Conserva la misma
   *  semántica que en la sesión suelta: un lote presente fue monitoreado aunque
   *  su `inc` venga `{}`; uno ausente, no. */
  datos: DatosMonitoreo;
};

/**
 * Agrupa las sesiones del histórico por semana ISO.
 *
 * Sin esto el eje del gráfico lleva una etiqueta por sesión, y una semana con
 * varios recorridos aparece repetida ("Sem 34" tres veces) como si fueran
 * momentos distintos. Además, cuando el recorrido de una semana se reparte
 * entre días —unos lotes el lunes, otros el miércoles— cada lote queda con
 * huecos en las sesiones donde no le tocaba, y la serie se ve entrecortada sin
 * que falte ningún dato.
 *
 * **Cómo combina dos recorridos del mismo lote en una misma semana:** vale el
 * **último**. Se prefiere sobre promediarlos porque así el gráfico solo muestra
 * valores que se midieron de verdad, en vez de un intermedio que nunca existió.
 * En la operación normal la regla casi no se aplica: hay un recorrido por lote
 * por semana, y las tres reglas posibles darían el mismo resultado.
 *
 * El mapa de plagas de un lote se reemplaza **entero**, no plaga por plaga: si
 * el último recorrido lo encontró limpio (`{}`), esa es la lectura de la
 * semana, y fusionar por plaga arrastraría detecciones ya superadas.
 *
 * @param sesiones Sesiones del histórico (en cualquier orden).
 * @returns Semanas ordenadas de la más vieja a la más nueva.
 */
export function agruparPorSemana(
  sesiones: SesionDashboardItem[],
): SemanaMonitoreo[] {
  const ordenadas = [...sesiones].sort((a, b) => a.fecha.localeCompare(b.fecha));
  const grupos = new Map<string, SemanaMonitoreo>();

  for (const sesion of ordenadas) {
    const anio = _anioIso(sesion.fecha);
    const clave = `${anio}-${sesion.semana}`;

    let grupo = grupos.get(clave);
    if (!grupo) {
      const lunes = _lunesDeLaSemana(sesion.fecha);
      grupo = {
        anio,
        semana: sesion.semana,
        fecha: sesion.fecha,
        inicio: _aIso(lunes),
        fin: _aIso(_sumarDias(lunes, 6)),
        sesiones: [],
        tieneSinFinalizar: false,
        datos: {},
      };
      grupos.set(clave, grupo);
    }

    grupo.sesiones.push(sesion);
    // Las sesiones llegan en orden ascendente, así que la última que se procesa
    // es la más reciente de la semana.
    grupo.fecha = sesion.fecha;
    if (!sesion.isFinalizada) grupo.tieneSinFinalizar = true;

    for (const [loteId, lote] of Object.entries(sesion.datos ?? {})) {
      // Reemplazo del registro COMPLETO del lote, no una fusión campo por campo:
      // `inc`, `afectados` y `muestra` describen la misma medición, y mezclar los
      // conteos de un recorrido con la muestra de otro daría una incidencia que
      // no ocurrió. El último recorrido de la semana gana entero.
      grupo.datos[loteId] = lote;
    }
  }

  return [...grupos.values()].sort(
    (a, b) => a.anio - b.anio || a.semana - b.semana,
  );
}

/**
 * Año ISO de una fecha `yyyy-MM-dd`.
 *
 * No alcanza con leer los cuatro primeros caracteres: la semana ISO 1 puede
 * empezar el 29 de diciembre, y la 52/53 puede terminar el 3 de enero. Tomando
 * el año del calendario, una misma semana ISO a caballo entre dos años se
 * partiría en dos grupos — exactamente el problema que este módulo resuelve.
 *
 * Se resuelve por el jueves: por definición, el año ISO de una semana es el año
 * calendario en el que cae su jueves.
 */
function _anioIso(fecha: string): number {
  const [anio, mes, dia] = fecha.slice(0, 10).split("-").map(Number);
  const referencia = new Date(Date.UTC(anio, mes - 1, dia));
  // getUTCDay: 0 = domingo. En ISO la semana va de lunes (1) a domingo (7).
  const diaIso = referencia.getUTCDay() || 7;
  referencia.setUTCDate(referencia.getUTCDate() + 4 - diaIso);
  return referencia.getUTCFullYear();
}

/**
 * Lo que se sabe de un lote en un recorrido.
 *
 * Los tres campos viajan juntos porque describen la misma medición y **se
 * reemplazan juntos** al fusionar la semana: mezclar los conteos de un recorrido
 * con la muestra de otro daría una incidencia que no ocurrió.
 */
export type LoteMonitoreado = {
  /** `inc[plagaId]` = promedio de individuos por árbol. */
  inc: Record<string, number>;
  /** `afectados[plagaId]` = árboles de la muestra con la plaga presente. Es la
   *  otra mitad de `inc`: con el promedio solo no se distingue 6 ácaros en cada
   *  árbol de 240 en uno. La incidencia sale de `afectados / muestra`. */
  afectados: Record<string, number>;
  /** Árboles evaluados en el lote. Es el divisor de las dos métricas. */
  muestra: number;
  /** `referencias[plagaId]` = con qué valores se evaluó ese organismo, tal como
   *  estaban el día de la sesión.
   *
   *  **Es una copia congelada, no la configuración vigente.** Si el cliente sube
   *  un umbral en octubre, los informes de agosto tienen que seguir diciendo lo
   *  mismo; leyendo los valores de hoy, un lote que ese día estaba en rojo
   *  aparecería en amarillo sin que nada en campo hubiera cambiado.
   *
   *  Un organismo **ausente** de este mapa no tiene referencia congelada: pasa
   *  con las sesiones anteriores a que el servidor empezara a guardarlas, y se
   *  resuelve cayendo a los valores vigentes del cultivo. */
  referencias: Record<string, MonitoreoReferencia>;
  /** Cultivo del lote. Lo necesita el camino de compatibilidad: sin referencias
   *  congeladas, los valores vigentes se resuelven por cultivo, y sin el cultivo
   *  solo queda el umbral compartido del catálogo viejo. */
  cultivoId?: string;
};

/** Mediciones indexadas: `datos[loteId]`. Es la forma que consumen el mapa y la
 *  barra de plagas, vengan de una semana entera o de un recorrido. */
export type DatosMonitoreo = Record<string, LoteMonitoreado>;

/**
 * Datos a mostrar según la selección del sidebar.
 *
 * Sin `sesionId` vale la semana completa (los recorridos ya fusionados). Con
 * uno, vale **solo ese recorrido**: así el mapa responde "qué se hizo el lunes"
 * dejando transparentes los lotes que ese día no se visitaron.
 *
 * No hace falta pedir nada al servidor: al agrupar por semana se conservan las
 * sesiones individuales con sus propios `datos`.
 */
export function getDatosDeRecorrido(
  semana: SemanaMonitoreo | null,
  sesionId: string | null,
): DatosMonitoreo | null {
  if (!semana) return null;
  if (!sesionId) return semana.datos;
  return (
    semana.sesiones.find((sesion) => sesion.id === sesionId)?.datos ??
    semana.datos
  );
}

/** Lunes de la semana ISO a la que pertenece una fecha, en UTC. */
function _lunesDeLaSemana(fecha: string): Date {
  const [anio, mes, dia] = fecha.slice(0, 10).split("-").map(Number);
  const referencia = new Date(Date.UTC(anio, mes - 1, dia));
  // getUTCDay: 0 = domingo. En ISO la semana va de lunes (1) a domingo (7).
  const diaIso = referencia.getUTCDay() || 7;
  return _sumarDias(referencia, 1 - diaIso);
}

/** Suma días a una fecha UTC sin mutar la original. */
function _sumarDias(fecha: Date, dias: number): Date {
  const resultado = new Date(fecha.getTime());
  resultado.setUTCDate(resultado.getUTCDate() + dias);
  return resultado;
}

/** Fecha UTC a `yyyy-MM-dd`. */
function _aIso(fecha: Date): string {
  return fecha.toISOString().slice(0, 10);
}

/**
 * Etiqueta compacta de un recorrido: "24 ago".
 *
 * Vive acá, junto a {@link formatRangoSemana}, porque la usan las dos pantallas
 * que ofrecen elegir un día dentro de una semana —el sidebar del mapa y la del
 * informe— y las dos tienen que nombrar la misma fecha igual.
 *
 * @param isoDate Fecha en `yyyy-MM-dd` (o ISO completa: se recorta).
 */
export function formatDiaMes(isoDate: string): string {
  const [, mes, dia] = isoDate.slice(0, 10).split("-").map(Number);
  if (!mes || !dia) return isoDate;
  return `${dia} ${MESES_ABR[mes - 1]}`;
}

/**
 * Rango de calendario de la semana, para etiquetarla: "3–9 ago", o
 * "31 ago – 6 sep" cuando cruza de mes.
 *
 * Describe **la semana**, no sus recorridos: es un hecho del calendario y no
 * cambia según cuántas salidas hubo ni cuándo fueron. Poner ahí la fecha de un
 * recorrido —como se hacía antes— mezclaba dos cosas distintas y daba a
 * entender que la semana era ese día. Las fechas reales de las salidas se
 * informan dentro del reporte, que es donde son la información.
 */
export function formatRangoSemana(semana: SemanaMonitoreo): string {
  const [, mesInicio, diaInicio] = semana.inicio.split("-").map(Number);
  const [, mesFin, diaFin] = semana.fin.split("-").map(Number);

  return mesInicio === mesFin
    ? `${diaInicio}–${diaFin} ${MESES_ABR[mesFin - 1]}`
    : `${diaInicio} ${MESES_ABR[mesInicio - 1]} – ${diaFin} ${MESES_ABR[mesFin - 1]}`;
}

/**
 * Etiquetas del eje de tiempo, una por semana.
 *
 * Agrega el año solo cuando el rango abarca más de uno: con "Todo el historial"
 * habría dos "Sem 34" de años distintos, que es la misma confusión que
 * agrupar vino a resolver.
 */
export function buildEtiquetasSemana(semanas: SemanaMonitoreo[]): string[] {
  const variosAnios = new Set(semanas.map((s) => s.anio)).size > 1;
  return semanas.map((s) =>
    variosAnios
      ? `Sem ${s.semana} '${String(s.anio).slice(2)}`
      : `Sem ${s.semana}`,
  );
}
